import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Disc3, Users, Mail, Settings, LogOut, ExternalLink, Newspaper, SlidersHorizontal, MessageCircle, ShoppingBag, Megaphone, TrendingUp } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/site/Logo";
import { AdminReleases } from "@/components/admin/AdminReleases";
import { AdminArtists } from "@/components/admin/AdminArtists";
import { AdminMessages } from "@/components/admin/AdminMessages";
import { AdminSettings } from "@/components/admin/AdminSettings";
import { AdminSubscribers } from "@/components/admin/AdminSubscribers";
import { AdminMastering } from "@/components/admin/AdminMastering";
import { AdminChats } from "@/components/admin/AdminChats";
import { AdminOrders } from "@/components/admin/AdminCommerce";
import { AdminPromo } from "@/components/admin/AdminPromo";
import { AdminTopTracks } from "@/components/admin/AdminTopTracks";

const TABS = [
  { id: "releases", label: "Uscite", Icon: Disc3 },
  { id: "mastering", label: "Mastering", Icon: SlidersHorizontal },
  { id: "promo", label: "Promozione", Icon: Megaphone },
  { id: "top", label: "Classifica", Icon: TrendingUp },
  { id: "artists", label: "Artisti", Icon: Users },
  { id: "messages", label: "Messaggi", Icon: Mail },
  { id: "chats", label: "Chat", Icon: MessageCircle },
  { id: "orders", label: "Ordini", Icon: ShoppingBag },
  { id: "subscribers", label: "Newsletter", Icon: Newspaper },
  { id: "settings", label: "Impostazioni", Icon: Settings },
];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("releases");
  const [stats, setStats] = useState({});
  const loadStats = () => api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  useEffect(() => { loadStats(); }, []);

  const STAT = [["Uscite", stats.releases], ["Brani", stats.tracks], ["Artisti", stats.artists], ["Da leggere", stats.unread], ["Iscritti", stats.subscribers]];

  return (
    <div className="min-h-screen bg-[#050507]" data-testid="admin-dashboard">
      <header className="border-b border-white/10 bg-[#0a0b10]">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Logo small />
          <div className="flex items-center gap-2">
            <Link to="/" target="_blank" className="hidden sm:inline-flex items-center gap-1 text-xs font-mono-tga uppercase tracking-[0.15em] text-zinc-400 hover:text-white px-3" data-testid="admin-view-site-link">Vedi sito <ExternalLink className="h-3 w-3" /></Link>
            <span className="hidden md:inline text-xs text-zinc-500">{user?.email}</span>
            <button onClick={logout} className="p-2 text-zinc-400 hover:text-rose-400" aria-label="Esci" data-testid="admin-logout-btn"><LogOut className="h-4 w-4" /></button>
          </div>
        </div>
      </header>
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
        <p className="font-mono-tga text-xs uppercase tracking-[0.3em] text-emerald-400">Pannello di gestione</p>
        <h1 className="font-display text-3xl sm:text-4xl font-black uppercase tracking-tighter mt-3">Ciao, boss.</h1>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-8">
          {STAT.map(([l, v]) => (
            <div key={l} className="border border-white/10 bg-[#0d0e12] p-5" data-testid={`admin-stat-${l.toLowerCase().replace(" ", "-")}`}>
              <p className="font-mono-tga text-[10px] uppercase tracking-[0.2em] text-zinc-500">{l}</p>
              <p className="font-display text-3xl font-black mt-2">{v ?? "–"}</p>
            </div>
          ))}
        </div>
        <nav className="flex gap-1 mt-10 border-b border-white/10 overflow-x-auto">
          {TABS.map(({ id, label, Icon }) => (
            <button key={id} onClick={() => setTab(id)} data-testid={`admin-tab-${id}`}
              className={`inline-flex items-center gap-2 px-5 py-3 text-sm whitespace-nowrap border-b-2 -mb-px transition-colors ${tab === id ? "border-emerald-500 text-white" : "border-transparent text-zinc-400 hover:text-white"}`}>
              <Icon className="h-4 w-4" />{label}{id === "messages" && stats.unread > 0 && <span className="ml-1 h-5 min-w-5 px-1.5 rounded-full bg-emerald-500 text-black text-[10px] grid place-items-center">{stats.unread}</span>}{id === "chats" && stats.chats_unread > 0 && <span className="ml-1 h-5 min-w-5 px-1.5 rounded-full bg-hot text-black text-[10px] grid place-items-center" data-testid="admin-chats-badge">{stats.chats_unread}</span>}
            </button>
          ))}
        </nav>
        <div className="pt-8 pb-32">
          {tab === "releases" && <AdminReleases onChange={loadStats} />}
          {tab === "artists" && <AdminArtists onChange={loadStats} />}
          {tab === "messages" && <AdminMessages onChange={loadStats} />}
          {tab === "promo" && <AdminPromo />}
          {tab === "top" && <AdminTopTracks />}
          {tab === "orders" && <AdminOrders />}
          {tab === "chats" && <AdminChats onChange={loadStats} />}
          {tab === "mastering" && <AdminMastering />}
          {tab === "subscribers" && <AdminSubscribers onChange={loadStats} />}
          {tab === "settings" && <AdminSettings />}
        </div>
      </div>
    </div>
  );
}
